import React, { useEffect, useRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { XIcon, CheckCircleIcon } from 'lucide-react';
interface SuccessModalProps {
  onClose: () => void;
  title?: string;
  message?: string;
}
export function SuccessModal({
  onClose,
  title = 'Payment Processed',
  message = "The customer's card has been successfully charged."
}: SuccessModalProps) {
  const {
    t
  } = useTranslation('common');
  const titleId = useId();
  const descId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Save and restore focus, lock body scroll, handle Esc + focus trap
  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Move focus into dialog
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
    focusable?.[0]?.focus();
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const items = dialogRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [onClose]);
  return createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
    margin: 0,
    background: 'rgba(17,24,39,0.5)'
  }} onClick={onClose} data-id="element-15459" data-mp="3qhwm71ocqs5s">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descId} className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-15460" data-mp="2xu0wwi1442j1">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-15461" data-mp="32r93rmgt8k4z">
          <h3 id={titleId} className="text-base font-semibold text-gray-900" data-id="element-15462" data-mp="n0elrfawsnfe">
            {title}
          </h3>
          <button type="button" onClick={onClose} aria-label={t('actions.close', {
          defaultValue: 'Close'
        })} className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded" data-id="element-15463" data-mp="i2d14pxu4g1t">
            <XIcon size={18} aria-hidden="true" data-id="element-15464" data-mp="1r0l6u43hn9ke" />
          </button>
        </div>
        <div className="p-6 flex flex-col items-center" data-id="element-15465" data-mp="1vy4ntbrkm0g1">
          <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mb-4" aria-hidden="true" data-id="element-15466" data-mp="1w0qtf5uwi5vm">
            <CheckCircleIcon size={28} className="text-green-700" data-id="element-15467" data-mp="2nor7yh17bjtx" />
          </div>
          <p id={descId} className="text-center text-sm text-gray-700 mb-5" data-id="element-15468" data-mp="29yxetminydee">
            {message}
          </p>
          <button type="button" onClick={onClose} className="px-5 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-15469" data-mp="2ewgvsu6qx4a1">
            {t('ui.SuccessModal.done')}
          </button>
        </div>
      </div>
    </div>, document.body);
}